/**
 * Contact Directory Page
 * Comprehensive contact management with organization alignment,
 * import/export, scoring, and contact-to-user conversion
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import PhoneInput from '@/components/ui/PhoneInput';
import {
  Users,
  Plus,
  Search,
  Filter,
  Upload,
  Download,
  MoreVertical,
  Mail,
  Phone,
  Building2,
  UserPlus,
  Tag,
  Trash2,
  Edit,
  Eye,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Star,
  FileSpreadsheet,
  Loader2,
  Link2,
  Cloud,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  contactsApi,
  organizationsApi,
  DirectoryContact,
  ContactFilters as ApiContactFilters,
  ContactCreateData,
  Organization,
} from '../../shared/services/platformApi';
import {
  tvTokens,
  TvButton,
  TvPageBar,
} from '../../shared/components/triveny';
import { config as productConfig } from '@/src/config';

// ============================================================================
// TYPES
// ============================================================================

type ContactTab = 'all' | 'active' | 'leads' | 'customers' | 'conversion-ready';

// Use DirectoryContact from API as Contact type
type Contact = DirectoryContact;

interface ContactFilters {
  search: string;
  organization_id?: number;
  lifecycle_stage?: string;
  status?: string;
  score_min?: number;
  score_max?: number;
  tags?: string[];
}

// No fallback data - API is source of truth

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600 bg-green-100';
  if (score >= 60) return 'text-yellow-600 bg-yellow-100';
  if (score >= 40) return 'text-orange-600 bg-orange-100';
  return 'text-red-600 bg-red-100';
}

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Hot';
  if (score >= 60) return 'Warm';
  if (score >= 40) return 'Cool';
  return 'Cold';
}

function getStatusColor(status: string): string {
  switch (status) {
    // 4-value lifecycle (post migration 286)
    case 'ACTIVE':   return 'bg-green-100 text-green-800';
    case 'TRIAL':    return 'bg-orange-100 text-orange-800';
    case 'DEMO':     return 'bg-purple-100 text-purple-800';
    case 'INACTIVE': return 'bg-gray-100 text-gray-700';
    // Legacy values, kept for backward-compat with cached data
    case 'CUSTOMER': return 'bg-green-100 text-green-800';
    case 'OPPORTUNITY': return 'bg-purple-100 text-purple-800';
    case 'PROSPECT': return 'bg-purple-100 text-purple-800';
    case 'CHURNED':  return 'bg-gray-100 text-gray-700';
    default: return 'bg-gray-100 text-gray-600';
  }
}

function getLifecycleColor(stage: string): string {
  switch (stage) {
    case 'customer': return 'bg-green-100 text-green-800';
    case 'evangelist': return 'bg-purple-100 text-purple-800';
    case 'opportunity': return 'bg-blue-100 text-blue-800';
    case 'sql': return 'bg-indigo-100 text-indigo-800';
    case 'mql': return 'bg-yellow-100 text-yellow-800';
    case 'lead': return 'bg-orange-100 text-orange-800';
    case 'contact': return 'bg-sky-100 text-sky-800';
    case 'subscriber': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-600';
  }
}

// ============================================================================
// MODAL COMPONENTS
// ============================================================================

interface CreateContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (contact: Partial<Contact>) => void;
  organizations: Organization[];
}

function CreateContactModal({ isOpen, onClose, onSubmit, organizations }: CreateContactModalProps) {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    mobile_phone: '',
    company_name: '',
    job_title: '',
    department: '',
    organization_id: '',
    // Marketing fields
    score: 0,
    lifecycle_stage: 'subscriber',
    status: 'active',
    tags: '',
    // Communication preferences
    do_not_email: false,
    do_not_call: false,
    do_not_sms: false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      cbo_id: formData.organization_id ? parseInt(formData.organization_id) : undefined,
      tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      mobile_phone: '',
      company_name: '',
      job_title: '',
      department: '',
      organization_id: '',
      score: 0,
      lifecycle_stage: 'subscriber',
      status: 'active',
      tags: '',
      do_not_email: false,
      do_not_call: false,
      do_not_sms: false,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Contact</DialogTitle>
          <DialogDescription>
            Create a new contact record. Link to an organization for ABM tracking.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Basic Info Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <PhoneInput
                  value={formData.phone}
                  onChange={(v) => setFormData({ ...formData, phone: v })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Phone</label>
                <PhoneInput
                  value={formData.mobile_phone}
                  onChange={(v) => setFormData({ ...formData, mobile_phone: v })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Organization Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Organization</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
              <select
                value={formData.organization_id}
                onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select organization...</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>
                    {org.name} ({org.customer_status})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
              <input
                type="text"
                value={formData.job_title}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Marketing Status Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Marketing Status</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Score</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.score}
                  onChange={(e) => setFormData({ ...formData, score: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lifecycle Stage</label>
                <select
                  value={formData.lifecycle_stage}
                  onChange={(e) => setFormData({ ...formData, lifecycle_stage: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="contact">Contact</option>
                  <option value="subscriber">Subscriber</option>
                  <option value="lead">Lead</option>
                  <option value="mql">Marketing Qualified</option>
                  <option value="sql">Sales Qualified</option>
                  <option value="opportunity">Opportunity</option>
                  <option value="customer">Customer</option>
                  <option value="evangelist">Evangelist</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="bounced">Bounced</option>
                  <option value="unsubscribed">Unsubscribed</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="Enter tags separated by commas"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
            </div>
          </div>

          {/* Communication Preferences */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Communication Preferences</h3>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.do_not_email}
                  onChange={(e) => setFormData({ ...formData, do_not_email: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Do not email</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.do_not_call}
                  onChange={(e) => setFormData({ ...formData, do_not_call: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Do not call</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.do_not_sms}
                  onChange={(e) => setFormData({ ...formData, do_not_sms: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Do not SMS</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add Contact
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// EDIT CONTACT MODAL
// ============================================================================

interface EditContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact | null;
  onSubmit: (contactId: string, updates: Partial<Contact>) => void;
  organizations: Organization[];
}

function EditContactModal({ isOpen, onClose, contact, onSubmit, organizations }: EditContactModalProps) {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    mobile_phone: '',
    company_name: '',
    job_title: '',
    department: '',
    organization_id: '',
    // Address fields
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    // Marketing fields
    score: 0,
    lifecycle_stage: 'subscriber',
    status: 'active',
    tags: '',
    // Communication preferences
    do_not_email: false,
    do_not_call: false,
    do_not_sms: false,
    // Social
    linkedin_url: '',
    twitter_handle: '',
    preferred_language: 'en',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Populate form when contact changes
  useEffect(() => {
    if (contact) {
      setFormData({
        email: contact.email || '',
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        phone: contact.phone || '',
        mobile_phone: contact.mobile_phone || '',
        company_name: contact.company_name || '',
        job_title: contact.job_title || '',
        department: contact.department || '',
        organization_id: contact.cbo_id?.toString() || '',
        address_line1: contact.address_line1 || '',
        address_line2: contact.address_line2 || '',
        city: contact.city || '',
        state: contact.state || '',
        postal_code: contact.postal_code || '',
        country: contact.country || '',
        score: contact.score || 0,
        lifecycle_stage: contact.lifecycle_stage || 'subscriber',
        status: contact.status || 'active',
        tags: contact.tags?.join(', ') || '',
        do_not_email: contact.do_not_email || false,
        do_not_call: contact.do_not_call || false,
        do_not_sms: contact.do_not_sms || false,
        linkedin_url: contact.linkedin_url || '',
        twitter_handle: contact.twitter_handle || '',
        preferred_language: contact.preferred_language || 'en',
      });
    }
  }, [contact]);

  if (!contact) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const updates: Partial<Contact> = {
        ...formData,
        cbo_id: formData.organization_id ? parseInt(formData.organization_id) : undefined,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [],
        updated_at: new Date().toISOString(),
      };
      await onSubmit(contact.id, updates);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Contact</DialogTitle>
          <DialogDescription>
            Update contact information and marketing status.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Basic Info Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <PhoneInput
                  value={formData.phone}
                  onChange={(v) => setFormData({ ...formData, phone: v })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Phone</label>
                <PhoneInput
                  value={formData.mobile_phone}
                  onChange={(v) => setFormData({ ...formData, mobile_phone: v })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Language</label>
              <select
                value={formData.preferred_language}
                onChange={(e) => setFormData({ ...formData, preferred_language: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="zh">Chinese</option>
              </select>
            </div>
          </div>

          {/* Address Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Address</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
              <input
                type="text"
                value={formData.address_line1}
                onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
              <input
                type="text"
                value={formData.address_line2}
                onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Organization Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Organization</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
              <select
                value={formData.organization_id}
                onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select organization...</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>
                    {org.name} ({org.customer_status})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                <input
                  type="text"
                  value={formData.job_title}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Social Profiles */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Social Profiles</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                <input
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/in/username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Twitter Handle</label>
                <input
                  type="text"
                  value={formData.twitter_handle}
                  onChange={(e) => setFormData({ ...formData, twitter_handle: e.target.value })}
                  placeholder="@username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Marketing Status Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Marketing Status</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Score</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.score}
                    onChange={(e) => setFormData({ ...formData, score: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${getScoreColor(formData.score)}`}>
                    {getScoreLabel(formData.score)}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lifecycle Stage</label>
                <select
                  value={formData.lifecycle_stage}
                  onChange={(e) => setFormData({ ...formData, lifecycle_stage: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="contact">Contact</option>
                  <option value="subscriber">Subscriber</option>
                  <option value="lead">Lead</option>
                  <option value="mql">Marketing Qualified</option>
                  <option value="sql">Sales Qualified</option>
                  <option value="opportunity">Opportunity</option>
                  <option value="customer">Customer</option>
                  <option value="evangelist">Evangelist</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="bounced">Bounced</option>
                  <option value="unsubscribed">Unsubscribed</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="Enter tags separated by commas"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
            </div>
          </div>

          {/* Communication Preferences */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Communication Preferences</h3>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.do_not_email}
                  onChange={(e) => setFormData({ ...formData, do_not_email: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Do not email</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.do_not_call}
                  onChange={(e) => setFormData({ ...formData, do_not_call: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Do not call</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.do_not_sms}
                  onChange={(e) => setFormData({ ...formData, do_not_sms: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Do not SMS</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ConvertToUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact | null;
  onConvert: (contactId: string, role: string, sendWelcomeEmail: boolean) => void;
}

function ConvertToUserModal({ isOpen, onClose, contact, onConvert }: ConvertToUserModalProps) {
  const [role, setRole] = useState('viewer');
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);

  if (!contact) return null;

  const handleConvert = () => {
    onConvert(contact.id, role, sendWelcomeEmail);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Convert Contact to User</DialogTitle>
          <DialogDescription>
            Promote this contact to a platform user with access to the organization.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-semibold">
                  {contact.first_name[0]}{contact.last_name[0]}
                </span>
              </div>
              <div>
                <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                <p className="text-sm text-gray-500">{contact.email}</p>
              </div>
            </div>
            {contact.organization_name && (
              <div className="mt-3 flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4 text-gray-400" />
                <span>{contact.organization_name}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(contact.organization_status || '')}`}>
                  {contact.organization_status}
                </span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="viewer">Viewer (Read-only access)</option>
              <option value="staff">Staff (Standard access)</option>
              <option value="manager">Manager (Team oversight)</option>
              <option value="org_admin">Organization Admin (Full org access)</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sendWelcome"
              checked={sendWelcomeEmail}
              onChange={(e) => setSendWelcomeEmail(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="sendWelcome" className="text-sm text-gray-700">
              Send welcome email with login instructions
            </label>
          </div>

          {contact.organization_status !== 'ACTIVE' && contact.organization_status !== 'CUSTOMER' && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                <div className="text-sm text-yellow-700">
                  <p className="font-medium">Organization not yet a customer</p>
                  <p>This contact's organization is currently "{contact.organization_status}".
                     Consider closing the deal first before converting contacts to users.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConvert}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Convert to User
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ImportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (file: File, options: { duplicateHandling: string }) => void;
}

function ImportContactsModal({ isOpen, onClose, onImport }: ImportContactsModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [duplicateHandling, setDuplicateHandling] = useState('skip');
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.csv') || droppedFile.name.endsWith('.xlsx'))) {
      setFile(droppedFile);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleImport = () => {
    if (file) {
      onImport(file, { duplicateHandling });
      setFile(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Import Contacts</DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel file to import contacts in bulk.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileSpreadsheet className="h-8 w-8 text-green-600" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button
                  onClick={() => setFile(null)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-2">Drag and drop your file here, or</p>
                <label className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium">
                  browse to upload
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </label>
                <p className="text-sm text-gray-500 mt-2">Supports CSV and Excel files</p>
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duplicate Handling
            </label>
            <select
              value={duplicateHandling}
              onChange={(e) => setDuplicateHandling(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="skip">Skip duplicates (keep existing)</option>
              <option value="update">Update existing records</option>
              <option value="create_new">Create new records anyway</option>
            </select>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
            <p className="font-medium mb-1">Expected columns:</p>
            <p>email (required), first_name, last_name, phone, company, job_title</p>
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!file}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Import Contacts
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ContactDirectoryPage() {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const navigate = useNavigate();

  // State - start empty, API will populate
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ContactTab>('all');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);

  // Detail view state
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [detailContact, setDetailContact] = useState<Contact | null>(null);

  // Filters
  const [filters, setFilters] = useState<ContactFilters>({ search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSyncMenu, setShowSyncMenu] = useState(false);
  const [outlookSyncing, setOutlookSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [visibilityFilter, setVisibilityFilter] = useState<string>('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showActionMenu, setShowActionMenu] = useState<string | null>(null);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Sorting
  const [sortField, setSortField] = useState<'name' | 'score' | 'last_activity' | 'created_at'>('last_activity');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch contacts from API
  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Build API filters based on current tab and filters
      const apiFilters: ApiContactFilters & { page?: number; page_size?: number; sort_by?: string; sort_order?: 'asc' | 'desc' } = {
        page: currentPage,
        page_size: pageSize,
        sort_by: sortField === 'name' ? 'first_name' : sortField,
        sort_order: sortOrder,
      };

      // Add search
      if (filters.search) {
        apiFilters.search = filters.search;
      }

      // Add organization filter — backend Query param is `organization_id`
      // (see contacts_router.py:286), NOT cbo_id. Previously sending cbo_id
      // would either be ignored (best case) or cause a 422 validation error.
      if (filters.organization_id) {
        (apiFilters as any).organization_id = filters.organization_id;
      }

      // Add lifecycle stage filter
      if (filters.lifecycle_stage) {
        apiFilters.lifecycle_stage = filters.lifecycle_stage as ApiContactFilters['lifecycle_stage'];
      }

      // Add status filter based on tab
      switch (activeTab) {
        case 'active':
          apiFilters.status = 'active';
          break;
        case 'leads':
          (apiFilters as any).lifecycle_stage = 'lead,mql,sql';
          break;
        case 'customers':
          (apiFilters as any).lifecycle_stage = 'customer';
          break;
        case 'conversion-ready':
          apiFilters.score_min = 80;
          apiFilters.status = 'active';
          break;
      }

      // Add score filters
      if (filters.score_min !== undefined) {
        apiFilters.score_min = filters.score_min;
      }
      if (filters.score_max !== undefined) {
        apiFilters.score_max = filters.score_max;
      }

      // Add source and visibility filters
      if (sourceFilter) {
        (apiFilters as any).source = sourceFilter;
      }
      if (visibilityFilter) {
        (apiFilters as any).visibility = visibilityFilter;
      }

      const response = await contactsApi.getContacts(apiFilters);

      if (response.success && response.data) {
        // Handle both 'items' and 'contacts' field names (backend returns 'contacts')
        const contactsList = response.data.items || response.data.contacts || [];
        setContacts(contactsList);
        setTotalCount(response.data.total || 0);
      } else {
        setError('Failed to load contacts from server.');
        setContacts([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      setError('Unable to connect to server. Please try again.');
      setContacts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, sortField, sortOrder, filters, activeTab, sourceFilter, visibilityFilter]);

  // Fetch contacts on mount and when dependencies change
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Debounce search input — wait 400ms after typing stops before triggering API call
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
        setCurrentPage(1);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch organizations on mount
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await organizationsApi.getOrganizations();
        if (response.success && response.data) {
          // Map CBO data to Organization format
          const orgs: Organization[] = response.data.map((cbo: any) => ({
            id: cbo.id,
            name: cbo.name || cbo.organization_name,
            short_name: cbo.short_name,
            customer_status: cbo.customer_status || 'DEMO',
            contact_email: cbo.contact_email,
            contact_phone: cbo.contact_phone,
            website: cbo.website,
            address: cbo.address,
          }));
          setOrganizations(orgs);
        }
      } catch (err) {
        console.error('Failed to fetch organizations:', err);
      }
    };
    fetchOrganizations();
  }, []);

  // Filter contacts based on tab and filters
  // Server handles filtering, sorting, and pagination — no client-side re-filtering
  const sortedContacts = contacts;

  // Stats — use totalCount from API for the main number, page data for breakdowns
  const stats = {
    total: totalCount,
    active: contacts.filter(c => c.status === 'active').length,
    leads: contacts.filter(c => ['lead', 'mql', 'sql'].includes(c.lifecycle_stage)).length,
    customers: contacts.filter(c => c.lifecycle_stage === 'customer').length,
    conversionReady: contacts.filter(c => (c.organization_status === 'ACTIVE' || c.organization_status === 'CUSTOMER') && !c.converted_to_user_id).length,
    averageScore: Math.round(contacts.reduce((sum, c) => sum + c.score, 0) / (contacts.length || 1)),
    synced: contacts.filter(c => c.source === 'outlook').length,
  };

  // Handlers
  const handleCreateContact = async (contactData: Partial<Contact>) => {
    try {
      const createData: ContactCreateData = {
        email: contactData.email || '',
        first_name: contactData.first_name || '',
        last_name: contactData.last_name || '',
        phone: contactData.phone,
        company_name: contactData.company_name,
        job_title: contactData.job_title,
        cbo_id: contactData.cbo_id,
      };

      const response = await contactsApi.createContact(createData);

      if (response.success && response.data) {
        // Refresh the contact list
        fetchContacts();
      } else {
        // Fallback: add locally
        const newContact: Contact = {
          id: `contact_${Date.now()}`,
          ...createData,
          organization_name: organizations.find(o => o.id === contactData.cbo_id)?.name,
          organization_status: organizations.find(o => o.id === contactData.cbo_id)?.customer_status,
          score: 0,
          lifecycle_stage: 'subscriber',
          status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        setContacts([newContact, ...contacts]);
      }
    } catch (err) {
      console.error('Failed to create contact:', err);
      setError('Failed to create contact. Please try again.');
    }
  };

  const handleUpdateContact = async (contactId: string, updates: Partial<Contact>) => {
    try {
      const response = await contactsApi.updateContact(contactId, updates);

      if (response.success && response.data) {
        // Refresh the contact list
        fetchContacts();
        // Update the detail panel if showing
        if (detailContact?.id === contactId) {
          setDetailContact({ ...detailContact, ...updates });
        }
      } else {
        // Fallback: update locally
        setContacts(contacts.map(c =>
          c.id === contactId ? { ...c, ...updates } : c
        ));
      }
    } catch (err) {
      console.error('Failed to update contact:', err);
      setError('Failed to update contact. Please try again.');
      throw err; // Re-throw so the modal knows the save failed
    }
  };

  const handleConvertToUser = async (contactId: string, role: string, sendWelcomeEmail: boolean) => {
    try {
      const response = await contactsApi.convertToUser(contactId, {
        role,
        send_welcome_email: sendWelcomeEmail,
      });

      if (response.success && response.data?.success) {
        // Refresh contacts to show updated status
        fetchContacts();
      } else {
        // Fallback: update locally
        setContacts(contacts.map(c => {
          if (c.id === contactId) {
            return {
              ...c,
              converted_to_user_id: Date.now(),
              converted_at: new Date().toISOString(),
              status: 'converted' as const,
            };
          }
          return c;
        }));
      }
    } catch (err) {
      console.error('Failed to convert contact to user:', err);
      setError('Failed to convert contact. Please try again.');
    }
  };

  const handleSyncOutlookContacts = async () => {
    setOutlookSyncing(true);
    setShowSyncMenu(false);
    setSyncStatus('Connecting to Outlook and fetching contacts... This may take a moment for large address books.');
    try {
      const response = await contactsApi.syncOutlookContacts();
      if (response.success && response.data) {
        const { synced, updated, skipped, total_fetched } = response.data;
        setSyncStatus(`Sync complete: ${total_fetched} fetched from Outlook, ${synced} new contacts added, ${updated} updated, ${skipped} skipped`);
        fetchContacts();
        setTimeout(() => setSyncStatus(null), 8000);
      } else {
        setSyncStatus(null);
        alert(response.error?.message || 'Outlook sync failed');
      }
    } catch (err) {
      console.error('Outlook sync failed:', err);
      setSyncStatus(null);
      alert('Outlook sync failed. Please try again.');
    } finally {
      setOutlookSyncing(false);
    }
  };

  const handleShareContact = async (contactId: string) => {
    try {
      const response = await contactsApi.shareContact(contactId);
      if (response.success) {
        setContacts(prev => prev.map(c => c.id === contactId ? { ...c, visibility: 'shared' } : c));
      }
    } catch (err) {
      console.error('Failed to share contact:', err);
    }
  };

  const handleUnshareContact = async (contactId: string) => {
    try {
      const response = await contactsApi.unshareContact(contactId);
      if (response.success) {
        setContacts(prev => prev.map(c => c.id === contactId ? { ...c, visibility: 'private' } : c));
      }
    } catch (err) {
      console.error('Failed to unshare contact:', err);
    }
  };

  const handleBulkShare = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const response = await contactsApi.bulkShareContacts(ids);
      if (response.success) {
        setContacts(prev => prev.map(c => ids.includes(c.id) ? { ...c, visibility: 'shared' } : c));
        setSelectedIds(new Set());
        alert(`${ids.length} contacts shared with organization`);
      }
    } catch (err) {
      console.error('Failed to bulk share:', err);
    }
  };

  const handleConvertToLead = async (contactId: string) => {
    try {
      const response = await contactsApi.updateContact(contactId, { lifecycle_stage: 'lead' } as any);
      if (response.success) {
        setContacts(prev => prev.map(c => c.id === contactId ? { ...c, lifecycle_stage: 'lead' } : c));
      }
    } catch (err) {
      console.error('Failed to convert to lead:', err);
    }
  };

  const handleImportContacts = async (file: File, options: { duplicateHandling: string }) => {
    try {
      const response = await contactsApi.importCSV(file, {
        duplicate_handling: options.duplicateHandling as 'skip' | 'update' | 'create_new',
      });

      if (response.success && response.data) {
        const result = response.data;
        alert(`Import complete: ${result.created} created, ${result.updated} updated, ${result.skipped} skipped, ${result.errors.length} errors`);
        // Refresh contacts
        fetchContacts();
      } else {
        setError('Import failed. Please check your file format.');
      }
    } catch (err) {
      console.error('Failed to import contacts:', err);
      setError('Import failed. Please try again.');
    }
  };

  const handleExportContacts = async () => {
    try {
      const blob = await contactsApi.exportContacts('csv', {
        search: filters.search,
        cbo_id: filters.organization_id,
        lifecycle_stage: filters.lifecycle_stage as ApiContactFilters['lifecycle_stage'],
      });

      if (blob) {
        // Download the file
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contacts_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        setError('Export failed. Please try again.');
      }
    } catch (err) {
      console.error('Failed to export contacts:', err);
      setError('Export failed. Please try again.');
    }
  };

  const handleBulkTag = async (tags: string[]) => {
    if (selectedIds.size === 0) return;

    try {
      const response = await contactsApi.bulkTag(Array.from(selectedIds), tags);
      if (response.success) {
        fetchContacts();
        setSelectedIds(new Set());
      }
    } catch (err) {
      console.error('Failed to tag contacts:', err);
      setError('Failed to tag contacts. Please try again.');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    if (!confirm(`Are you sure you want to delete ${selectedIds.size} contacts?`)) {
      return;
    }

    try {
      const response = await contactsApi.bulkDelete(Array.from(selectedIds));
      if (response.success) {
        fetchContacts();
        setSelectedIds(new Set());
      }
    } catch (err) {
      console.error('Failed to delete contacts:', err);
      setError('Failed to delete contacts. Please try again.');
    }
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedContacts.map(c => c.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Triveny v2 chrome: when isTriveny, render a TvPageBar instead of the
  // legacy Tailwind h1 + button row. The page body below is untouched
  // (cards, filters, modals stay v1 for now — this PR only lifts the
  // page header into v2). Studio + Intelligence will get the same shape
  // in follow-ups.
  // ─────────────────────────────────────────────────────────────────────────
  const isTriveny = productConfig.isTriveny;

  return (
    <div
      className={isTriveny ? 'triveny-platform' : 'p-6 max-w-[1600px] mx-auto'}
      style={
        isTriveny
          ? {
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              background: tvTokens.paper,
              fontFamily: tvTokens.fontSans,
              color: tvTokens.ink,
            }
          : undefined
      }
    >
      {/* Triveny: no page banner (TvPillarBar shows "MARKET · Audiences"); the actions
          below move inline next to the tabs. */}
      <div
        className={isTriveny ? '' : ''}
        style={isTriveny ? { padding: '20px 22px', flex: 1, overflow: 'auto' } : undefined}
      >
      {/* Error Banner */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto p-1 hover:bg-red-100 rounded"
          >
            <X className="h-4 w-4 text-red-600" />
          </button>
        </div>
      )}

      {/* Header — v1 only. Triveny renders TvPageBar above. */}
      {!isTriveny && (
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            {!tabbed && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><Users className="h-6 w-6 text-blue-600" />{t('platform.contacts.title', 'Contact Directory')}</h1>}
            <p className="text-gray-500 mt-1">{t('platform.contacts.description', 'Manage contacts, track engagement, and convert to users')}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchContacts}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {t('common.refresh', 'Refresh')}
            </button>
            <button
              onClick={handleExportContacts}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              {t('common.export', 'Export')}
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Upload className="h-4 w-4" />
              {t('common.import', 'Import')}
            </button>

            {/* Sync Contacts Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSyncMenu(!showSyncMenu)}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Cloud className="h-4 w-4" />
                Sync
                <ChevronDown className="h-3 w-3" />
              </button>
              {showSyncMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowSyncMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                      Sync from External Sources
                    </div>
                    <button
                      onClick={() => {
                        sessionStorage.setItem('integration_entity_type', 'contacts_google');
                        window.dispatchEvent(new CustomEvent('changeTab', { detail: 'integrations' }));
                        setShowSyncMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-red-500">G</span>
                      Google Contacts
                    </button>
                    <button
                      onClick={handleSyncOutlookContacts}
                      disabled={outlookSyncing}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                    >
                      {outlookSyncing ? (
                        <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                      ) : (
                        <span className="w-5 h-5 flex items-center justify-center text-blue-500">M</span>
                      )}
                      {outlookSyncing ? 'Syncing Outlook...' : 'Microsoft Outlook/Exchange'}
                    </button>
                    <button
                      onClick={() => {
                        sessionStorage.setItem('integration_entity_type', 'contacts_crm');
                        window.dispatchEvent(new CustomEvent('changeTab', { detail: 'integrations' }));
                        setShowSyncMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Link2 className="h-4 w-4 text-purple-500" />
                      CRM (Salesforce, HubSpot)
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={() => {
                        sessionStorage.setItem('integration_entity_type', 'contacts_csv');
                        window.dispatchEvent(new CustomEvent('changeTab', { detail: 'integrations' }));
                        setShowSyncMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileSpreadsheet className="h-4 w-4 text-green-600" />
                      Import from CSV/vCard
                    </button>
                    <button
                      onClick={() => {
                        sessionStorage.setItem('integration_entity_type', 'contacts_ios');
                        window.dispatchEvent(new CustomEvent('changeTab', { detail: 'integrations' }));
                        setShowSyncMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-gray-600">🍎</span>
                      iOS/iCloud Contacts
                    </button>
                    <button
                      onClick={() => {
                        sessionStorage.setItem('integration_entity_type', 'contacts_android');
                        window.dispatchEvent(new CustomEvent('changeTab', { detail: 'integrations' }));
                        setShowSyncMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-green-600">🤖</span>
                      Android Contacts
                    </button>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              {t('platform.contacts.addContact', 'Add Contact')}
            </button>
          </div>
        </div>
      </div>
      )}{/* /!isTriveny header */}

      {/* Sync Status Banner */}
      {syncStatus && (
        <div className={`flex items-center gap-3 p-3 rounded-lg mb-4 ${outlookSyncing ? 'bg-blue-50 border border-blue-200' : 'bg-green-50 border border-green-200'}`}>
          {outlookSyncing && <Loader2 className="h-4 w-4 text-blue-600 animate-spin flex-shrink-0" />}
          {!outlookSyncing && <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />}
          <span className={`text-sm ${outlookSyncing ? 'text-blue-700' : 'text-green-700'}`}>{syncStatus}</span>
          {!outlookSyncing && (
            <button onClick={() => setSyncStatus(null)} className="ml-auto text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Stats Cards — v2 on Triveny: mono eyebrow + Inter Tight numeral + hairline border;
          WeaveIn keeps the original color-tinted Tailwind chrome. */}
      {isTriveny ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 12,
            marginBottom: 18,
          }}
        >
          {(
            [
              { label: 'Total contacts', value: stats.total, Icon: Users },
              { label: 'Active', value: stats.active, Icon: CheckCircle },
              { label: 'Leads', value: stats.leads, Icon: TrendingUp },
              { label: 'Customers', value: stats.customers, Icon: Star },
              { label: 'Conversion-ready', value: stats.conversionReady, Icon: UserPlus },
              { label: 'Avg. score', value: stats.averageScore, Icon: TrendingUp },
            ] as const
          ).map(({ label, value, Icon }) => (
            <div
              key={label}
              style={{
                background: tvTokens.surface,
                border: `1px solid ${tvTokens.border}`,
                borderRadius: tvTokens.rLg,
                padding: '14px 16px',
                boxShadow: tvTokens.shadow1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: tvTokens.fontMono,
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: tvTokens.ink500,
                  marginBottom: 6,
                }}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </div>
              <div
                style={{
                  fontFamily: tvTokens.fontDisplay,
                  fontSize: 26,
                  fontWeight: 600,
                  letterSpacing: '-0.025em',
                  color: tvTokens.ink,
                }}
              >
                {value}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Users className="h-4 w-4" />
              <span className="text-sm">{t('platform.contacts.stats.totalContacts', 'Total Contacts')}</span>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm">{t('platform.contacts.stats.active', 'Active')}</span>
            </div>
            <p className="text-2xl font-bold">{stats.active}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm">{t('platform.contacts.stats.leads', 'Leads')}</span>
            </div>
            <p className="text-2xl font-bold">{stats.leads}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <Star className="h-4 w-4" />
              <span className="text-sm">{t('platform.contacts.stats.customers', 'Customers')}</span>
            </div>
            <p className="text-2xl font-bold">{stats.customers}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 text-orange-600 mb-1">
              <UserPlus className="h-4 w-4" />
              <span className="text-sm">Conversion Ready</span>
            </div>
            <p className="text-2xl font-bold">{stats.conversionReady}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm">Avg. Score</span>
            </div>
            <p className="text-2xl font-bold">{stats.averageScore}</p>
          </div>
        </div>
      )}

      {/* Tabs — Triveny renders the page actions inline on the right of this row. */}
      <div
        className="flex items-center justify-between gap-3 mb-4 border-b"
        style={isTriveny ? { borderBottomColor: tvTokens.borderMuted } : undefined}
      >
        <div className="flex items-center gap-1 overflow-x-auto">
        {[
          { id: 'all', label: 'All Contacts', count: stats.total },
          { id: 'active', label: 'Active', count: stats.active },
          { id: 'leads', label: 'Leads', count: stats.leads },
          { id: 'customers', label: 'Customers', count: stats.customers },
          { id: 'conversion-ready', label: 'Conversion Ready', count: stats.conversionReady },
        ].map(tab => {
          const isActiveTabRow = activeTab === tab.id;
          if (isTriveny) {
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id as ContactTab); setCurrentPage(1); }}
                style={{
                  padding: '8px 14px',
                  fontFamily: tvTokens.fontDisplay,
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: '-0.005em',
                  color: isActiveTabRow ? tvTokens.ink : tvTokens.ink500,
                  background: 'transparent',
                  border: 0,
                  borderBottom: `2px solid ${isActiveTabRow ? tvTokens.accent : 'transparent'}`,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  transition: 'color .12s ease, border-color .12s ease',
                  whiteSpace: 'nowrap',
                }}
                aria-current={isActiveTabRow ? 'page' : undefined}
              >
                {tab.label}
                <span
                  style={{
                    fontFamily: tvTokens.fontMono,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    color: isActiveTabRow ? tvTokens.accentInk : tvTokens.ink500,
                    background: isActiveTabRow ? tvTokens.accentSoft : tvTokens.paper2,
                    borderRadius: 999,
                    padding: '1px 7px',
                  }}
                >
                  {tab.count}
                </span>
              </button>
            );
          }
          return (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id as ContactTab); setCurrentPage(1); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                isActiveTabRow
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-gray-100">
                {tab.count}
              </span>
            </button>
          );
        })}
        </div>
        {isTriveny && (
          <div className="flex items-center gap-2 flex-shrink-0 pb-1">
            <TvButton variant="ghost" icon={RefreshCw} onClick={fetchContacts} disabled={loading}>
              {t('common.refresh', 'Refresh')}
            </TvButton>
            <TvButton variant="ghost" icon={Download} onClick={handleExportContacts}>
              {t('common.export', 'Export')}
            </TvButton>
            <TvButton variant="ghost" icon={Upload} onClick={() => setShowImportModal(true)}>
              {t('common.import', 'Import')}
            </TvButton>
            <TvButton variant="ink" icon={Plus} onClick={() => setShowCreateModal(true)}>
              Add contact
            </TvButton>
          </div>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search contacts by name, email, or company..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={filters.organization_id || ''}
          onChange={(e) => { setFilters({ ...filters, organization_id: e.target.value ? parseInt(e.target.value) : undefined }); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All Organizations</option>
          {organizations.map(org => (
            <option key={org.id} value={org.id}>
              {org.name} ({org.customer_status})
            </option>
          ))}
        </select>
        {isTriveny ? (
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              borderRadius: tvTokens.r,
              border: `1px solid ${showFilters ? tvTokens.accent : tvTokens.border}`,
              background: showFilters ? tvTokens.accentSoft : tvTokens.surface,
              color: showFilters ? tvTokens.accentInk : tvTokens.ink,
              fontFamily: tvTokens.fontDisplay,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background .12s ease, border-color .12s ease',
            }}
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
        ) : (
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg ${
              showFilters ? 'bg-blue-50 border-blue-300 text-blue-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
        )}
        <select
          value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Sources</option>
          <option value="manual">Manual</option>
          <option value="outlook">Outlook</option>
          <option value="csv">CSV Import</option>
        </select>
        <select
          value={visibilityFilter}
          onChange={(e) => { setVisibilityFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Contacts</option>
          <option value="shared">Shared</option>
          <option value="private">My Private</option>
        </select>
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 sm:gap-4 mb-4 p-3 rounded-lg"
          style={
            isTriveny
              ? {
                  background: tvTokens.accentSoft,
                  border: `1px solid ${tvTokens.accent}`,
                  borderRadius: tvTokens.r,
                }
              : { backgroundColor: 'rgb(239 246 255)' /* bg-blue-50 */ }
          }
        >
          <span
            className="text-sm"
            style={isTriveny ? { color: tvTokens.accentInk, fontWeight: 600 } : { color: 'rgb(29 78 216)' /* text-blue-700 */ }}
          >
            {selectedIds.size} selected
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleBulkShare}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              <Users className="h-3.5 w-3.5" />
              Share with Org
            </button>
            <button className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50">
              <Tag className="h-3.5 w-3.5" />
              Add Tags
            </button>
            <button className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50">
              <Mail className="h-3.5 w-3.5" />
              Send Email
            </button>
            <button className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 bg-white border border-red-300 rounded hover:bg-red-50">
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          </div>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-sm"
            style={isTriveny ? { color: tvTokens.accentInk, fontWeight: 500 } : { color: 'rgb(37 99 235)' /* text-blue-600 */ }}
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Contacts Table */}
      <div
        className="rounded-lg overflow-x-auto"
        style={
          isTriveny
            ? {
                background: tvTokens.surface,
                border: `1px solid ${tvTokens.border}`,
                borderRadius: tvTokens.rLg,
                boxShadow: tvTokens.shadow1,
              }
            : { backgroundColor: 'white', border: '1px solid rgb(229 231 235)' }
        }
      >
        <table className="w-full min-w-[800px]">
          <thead style={isTriveny ? { background: tvTokens.paper2 } : { backgroundColor: 'rgb(249 250 251)' }}>
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedIds.size === sortedContacts.length && sortedContacts.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  Contact
                  {sortField === 'name' && (sortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Phone
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Location
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Organization
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('score')}
              >
                <div className="flex items-center gap-1">
                  Score
                  {sortField === 'score' && (sortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Lifecycle
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('last_activity')}
              >
                <div className="flex items-center gap-1">
                  Last Activity
                  {sortField === 'last_activity' && (sortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                </div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedContacts.map(contact => (
              <tr key={contact.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(contact.id)}
                    onChange={() => toggleSelect(contact.id)}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 font-semibold text-sm">
                        {contact.first_name?.[0] || ''}{contact.last_name?.[0] || ''}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {contact.first_name} {contact.last_name}
                        {contact.converted_to_user_id && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                            <Check className="h-3 w-3 mr-0.5" />
                            User
                          </span>
                        )}
                        {contact.source === 'outlook' && (
                          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 text-xs bg-blue-50 text-blue-600 rounded">
                            Outlook
                          </span>
                        )}
                        {contact.visibility === 'private' && (
                          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 text-xs bg-amber-50 text-amber-600 rounded">
                            Private
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">{contact.email}</p>
                      {contact.job_title && (
                        <p className="text-xs text-gray-400">{contact.job_title}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm">
                    {contact.phone ? (
                      <div className="flex items-center gap-1 text-gray-900">
                        <Phone className="h-3.5 w-3.5 text-gray-400" />
                        {contact.phone}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                    {contact.mobile_phone && contact.mobile_phone !== contact.phone && (
                      <div className="flex items-center gap-1 text-gray-500 text-xs mt-0.5">
                        <span className="text-gray-400">Mobile:</span> {contact.mobile_phone}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm">
                    {contact.city || contact.state ? (
                      <>
                        <p className="text-gray-900">
                          {[contact.city, contact.state].filter(Boolean).join(', ')}
                        </p>
                        {contact.country && contact.country !== 'USA' && (
                          <p className="text-xs text-gray-500">{contact.country}</p>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {contact.organization_name ? (
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-900">{contact.organization_name}</p>
                        <span className={`inline-block px-2 py-0.5 text-xs rounded ${getStatusColor(contact.organization_status || '')}`}>
                          {contact.organization_status}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-sm font-medium ${getScoreColor(contact.score)}`}>
                      {contact.score}
                    </span>
                    <span className="text-xs text-gray-500">{getScoreLabel(contact.score)}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-1 text-xs rounded capitalize ${getLifecycleColor(contact.lifecycle_stage)}`}>
                    {contact.lifecycle_stage}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {contact.last_activity ? (
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Clock className="h-3.5 w-3.5" />
                      {new Date(contact.last_activity).toLocaleDateString()}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {(contact.organization_status === 'ACTIVE' || contact.organization_status === 'CUSTOMER') && !contact.converted_to_user_id && (
                      <button
                        onClick={() => { setSelectedContact(contact); setShowConvertModal(true); }}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                        title="Convert to User"
                      >
                        <UserPlus className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => { setDetailContact(contact); setShowDetailPanel(true); }}
                      className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => { setSelectedContact(contact); setShowEditModal(true); }}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit Contact"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowActionMenu(showActionMenu === contact.id ? null : contact.id);
                        }}
                        className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"
                        title="More"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {showActionMenu === contact.id && (
                        <div
                          className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[100]"
                          style={{ position: 'absolute' }}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`mailto:${contact.email}`, '_blank');
                              setShowActionMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Mail className="h-4 w-4" />
                            Send Email
                          </button>
                          {contact.phone && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`tel:${contact.phone}`, '_blank');
                                setShowActionMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Phone className="h-4 w-4" />
                              Call Contact
                            </button>
                          )}
                          <div className="border-t border-gray-100 my-1" />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedContact(contact);
                              setShowEditModal(true);
                              setShowActionMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Edit className="h-4 w-4" />
                            Edit Contact
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetailContact(contact);
                              setShowDetailPanel(true);
                              setShowActionMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Eye className="h-4 w-4" />
                            View Details
                          </button>
                          {contact.lifecycle_stage === 'contact' && (
                            <>
                              <div className="border-t border-gray-100 my-1" />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConvertToLead(contact.id);
                                  setShowActionMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                              >
                                <TrendingUp className="h-4 w-4" />
                                Convert to Lead
                              </button>
                            </>
                          )}
                          {contact.lifecycle_stage !== 'customer' && (
                            <>
                              <div className="border-t border-gray-100 my-1" />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedContact(contact);
                                  setShowConvertModal(true);
                                  setShowActionMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                              >
                                <UserPlus className="h-4 w-4" />
                                Convert to User
                              </button>
                            </>
                          )}
                          {contact.source === 'outlook' && (
                            <>
                              <div className="border-t border-gray-100 my-1" />
                              {contact.visibility === 'private' ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleShareContact(contact.id);
                                    setShowActionMenu(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                                >
                                  <Users className="h-4 w-4" />
                                  Share with Organization
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnshareContact(contact.id);
                                    setShowActionMenu(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2"
                                >
                                  <X className="h-4 w-4" />
                                  Make Private
                                </button>
                              )}
                            </>
                          )}
                          <div className="border-t border-gray-100 my-1" />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Are you sure you want to delete ${contact.first_name} ${contact.last_name}?`)) {
                                // TODO: Call delete API
                                alert('Delete functionality coming soon');
                              }
                              setShowActionMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete Contact
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {sortedContacts.length === 0 && (
          <div className="p-12 text-center">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No contacts found</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or add a new contact</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-600">
            Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount} contacts
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: Math.min(Math.ceil(totalCount / pageSize), 7) }, (_, i) => {
              const totalPages = Math.ceil(totalCount / pageSize);
              let page: number;
              if (totalPages <= 7) {
                page = i + 1;
              } else if (currentPage <= 4) {
                page = i + 1;
              } else if (currentPage >= totalPages - 3) {
                page = totalPages - 6 + i;
              } else {
                page = currentPage - 3 + i;
              }
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1.5 text-sm rounded-lg ${
                    currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalCount / pageSize), p + 1))}
              disabled={currentPage >= Math.ceil(totalCount / pageSize)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Contact Detail Panel */}
      {showDetailPanel && detailContact && (
        <Dialog open={showDetailPanel} onOpenChange={setShowDetailPanel}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-lg">
                    {detailContact.first_name?.[0] || ''}{detailContact.last_name?.[0] || ''}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold">{detailContact.first_name} {detailContact.last_name}</h2>
                  <p className="text-sm text-gray-500 font-normal">{detailContact.job_title || 'No title'}</p>
                </div>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 mt-4">
              {/* Contact Info Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Email</p>
                    <p className="text-sm text-gray-900">{detailContact.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Phone</p>
                    <p className="text-sm text-gray-900">{detailContact.phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Mobile Phone</p>
                    <p className="text-sm text-gray-900">{detailContact.mobile_phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Preferred Language</p>
                    <p className="text-sm text-gray-900">{detailContact.preferred_language || 'English'}</p>
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Address
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 uppercase">Street Address</p>
                      <p className="text-sm text-gray-900">
                        {detailContact.address_line1 || '—'}
                        {detailContact.address_line2 && <><br />{detailContact.address_line2}</>}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">City</p>
                      <p className="text-sm text-gray-900">{detailContact.city || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">State</p>
                      <p className="text-sm text-gray-900">{detailContact.state || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Postal Code</p>
                      <p className="text-sm text-gray-900">{detailContact.postal_code || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Country</p>
                      <p className="text-sm text-gray-900">{detailContact.country || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Organization Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Organization
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Organization</p>
                      <p className="text-sm text-gray-900">{detailContact.organization_name || detailContact.company_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Status</p>
                      {detailContact.organization_status ? (
                        <span className={`inline-block px-2 py-0.5 text-xs rounded ${getStatusColor(detailContact.organization_status)}`}>
                          {detailContact.organization_status}
                        </span>
                      ) : (
                        <p className="text-sm text-gray-400">—</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Department</p>
                      <p className="text-sm text-gray-900">{detailContact.department || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Job Title</p>
                      <p className="text-sm text-gray-900">{detailContact.job_title || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social & Web Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  🔗 Social & Web
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">LinkedIn</p>
                      {detailContact.linkedin_url ? (
                        <a href={detailContact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                          View Profile
                        </a>
                      ) : (
                        <p className="text-sm text-gray-400">—</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Twitter</p>
                      <p className="text-sm text-gray-900">{detailContact.twitter_handle ? `@${detailContact.twitter_handle}` : '—'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Marketing Status Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Marketing Status
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Score</p>
                      <span className={`inline-flex items-center px-2 py-1 rounded text-sm font-medium ${getScoreColor(detailContact.score)}`}>
                        {detailContact.score} - {getScoreLabel(detailContact.score)}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Lifecycle Stage</p>
                      <span className={`inline-block px-2 py-1 text-xs rounded capitalize ${getLifecycleColor(detailContact.lifecycle_stage)}`}>
                        {detailContact.lifecycle_stage}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Status</p>
                      <p className="text-sm text-gray-900 capitalize">{detailContact.status}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Communication Preferences Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  📧 Communication Preferences
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      {detailContact.do_not_email ? (
                        <X className="h-4 w-4 text-red-500" />
                      ) : (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                      <span className="text-sm text-gray-700">Email {detailContact.do_not_email ? 'Opt-out' : 'OK'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {detailContact.do_not_call ? (
                        <X className="h-4 w-4 text-red-500" />
                      ) : (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                      <span className="text-sm text-gray-700">Call {detailContact.do_not_call ? 'Opt-out' : 'OK'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {detailContact.do_not_sms ? (
                        <X className="h-4 w-4 text-red-500" />
                      ) : (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                      <span className="text-sm text-gray-700">SMS {detailContact.do_not_sms ? 'Opt-out' : 'OK'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags Section */}
              {detailContact.tags && detailContact.tags.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {detailContact.tags.map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="text-xs text-gray-500 pt-4 border-t border-gray-200">
                <p>Created: {new Date(detailContact.created_at).toLocaleString()}</p>
                <p>Last Updated: {new Date(detailContact.updated_at).toLocaleString()}</p>
                {detailContact.last_activity && (
                  <p>Last Activity: {new Date(detailContact.last_activity).toLocaleString()}</p>
                )}
              </div>
            </div>

            <DialogFooter className="mt-6">
              <button
                onClick={() => setShowDetailPanel(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setSelectedContact(detailContact);
                  setShowDetailPanel(false);
                  setShowEditModal(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Edit Contact
              </button>
              {detailContact.organization_status === 'CUSTOMER' && !detailContact.converted_to_user_id && (
                <button
                  onClick={() => {
                    setSelectedContact(detailContact);
                    setShowDetailPanel(false);
                    setShowConvertModal(true);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  Convert to User
                </button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Modals */}
      <CreateContactModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateContact}
        organizations={organizations}
      />

      <ConvertToUserModal
        isOpen={showConvertModal}
        onClose={() => { setShowConvertModal(false); setSelectedContact(null); }}
        contact={selectedContact}
        onConvert={handleConvertToUser}
      />

      <EditContactModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedContact(null); }}
        contact={selectedContact}
        onSubmit={handleUpdateContact}
        organizations={organizations}
      />

      <ImportContactsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportContacts}
      />
      </div>{/* /content padding wrapper */}
    </div>
  );
}

export default ContactDirectoryPage;
